import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Edit3, Eye, EyeOff, Monitor, Plus, RefreshCw, Save, Send, X, Info,
} from 'lucide-react';
import {
  adminGetEmailTemplates,
  adminUpsertEmailTemplate,
  adminToggleEmailTemplate,
  adminPreviewEmailTemplate,
  adminSeedEmailTemplates,
  adminSendNewsletter,
} from '../../services/notificationService';
import { useNotification } from '../../notification/NotificationContext';
import './AdminDashboard.css';
import './AdminEmailTemplatesPage.css';

const blankTemplate = () => ({
  id: 0,
  slug: '',
  name: '',
  subject: '',
  description: '',
  htmlBody: '',
  isActive: true,
});

const previewPlaceholders = {
  userName: 'Preview User',
  userEmail: 'preview@test.com',
  planName: 'Pro',
  appName: 'SkillOn',
  appUrl: 'https://skillon.app',
};

const AdminEmailTemplatesPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);

  const load = () => {
    setLoading(true);
    adminGetEmailTemplates()
      .then((d) => setTemplates(Array.isArray(d) ? d : []))
      .catch((e) => showNotification(e.message || 'Failed to load templates', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await adminSeedEmailTemplates();
      showNotification('Default templates seeded.', 'success');
      load();
    } catch (e) {
      showNotification(e.message || 'Seeding failed', 'error');
    } finally {
      setSeeding(false);
    }
  };

  const openCreate = () => setDrawer(blankTemplate());
  const openEdit = (t) => setDrawer({ ...t });
  const closeDrawer = () => setDrawer(null);

  const updateField = (field, value) =>
    setDrawer((d) => ({ ...d, [field]: value }));

  const submit = async () => {
    if (!drawer?.slug?.trim() || !drawer?.name?.trim() || !drawer?.subject?.trim()) {
      showNotification('Slug, name and subject are required.', 'warning');
      return;
    }
    setSaving(true);
    try {
      await adminUpsertEmailTemplate({
        id: drawer.id || 0,
        slug: drawer.slug.trim(),
        name: drawer.name.trim(),
        subject: drawer.subject.trim(),
        description: drawer.description?.trim() || '',
        htmlBody: drawer.htmlBody || '',
        isActive: !!drawer.isActive,
      });
      showNotification('Template saved.', 'success');
      closeDrawer();
      load();
    } catch (e) {
      showNotification(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (t) => {
    try {
      await adminToggleEmailTemplate(t.id);
      setTemplates((arr) => arr.map((x) => (x.id === t.id ? { ...x, isActive: !x.isActive } : x)));
    } catch (e) {
      showNotification(e.message || 'Toggle failed', 'error');
    }
  };

  const handlePreview = async (t) => {
    setPreviewLoading(true);
    setPreviewHtml('<p style="font-family: sans-serif; padding: 24px;">Loading preview…</p>');
    try {
      const html = await adminPreviewEmailTemplate(t.slug, previewPlaceholders);
      setPreviewHtml(html || '<p style="padding: 24px;">No content returned.</p>');
    } catch (e) {
      setPreviewHtml(`<p style="padding: 24px; color: #b91c1c; font-family: sans-serif;">${e.message || 'Preview failed'}</p>`);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="adm-shell">
      <header className="adm-header">
        <div className="adm-logo">
          <span className="adm-logo-icon">📧</span>
          <span>Email Templates</span>
        </div>
        <div className="adm-header-right">
          <button className="aet-btn aet-btn-ghost" onClick={() => navigate('/admin/dashboard')}>
            ← Dashboard
          </button>
          <button className="adm-logout" onClick={handleLogout}>Logout →</button>
        </div>
      </header>

      <main className="aet-main">
        <div className="aet-toolbar">
          <div>
            <h1 className="aet-title">Email Templates</h1>
            <p className="aet-sub">Manage transactional email templates and send newsletters.</p>
          </div>
          <div className="aet-actions">
            <button className="aet-btn aet-btn-ghost" onClick={handleSeed} disabled={seeding}>
              <RefreshCw size={14} /> {seeding ? 'Seeding…' : 'Seed Defaults'}
            </button>
            <button className="aet-btn aet-btn-ghost" onClick={() => setShowNewsletter(true)}>
              <Send size={14} /> Send Newsletter
            </button>
            <button className="aet-btn aet-btn-primary" onClick={openCreate}>
              <Plus size={14} /> New Template
            </button>
          </div>
        </div>

        <SmtpNotice />

        <div className="aet-table-wrap">
          {loading ? (
            <div className="aet-loading">Loading templates…</div>
          ) : templates.length === 0 ? (
            <div className="aet-empty">
              No templates yet. Click <strong>Seed Defaults</strong> to populate starters.
            </div>
          ) : (
            <table className="aet-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Subject</th>
                  <th>Active</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id}>
                    <td><strong>{t.name}</strong></td>
                    <td><code className="aet-slug">{t.slug}</code></td>
                    <td className="aet-subj">{t.subject}</td>
                    <td>
                      <span className={`aet-pill ${t.isActive ? 'aet-pill-on' : 'aet-pill-off'}`}>
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="aet-icon-btn" title="Edit" onClick={() => openEdit(t)}>
                        <Edit3 size={15} />
                      </button>
                      <button className="aet-icon-btn" title="Toggle" onClick={() => handleToggle(t)}>
                        {t.isActive ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                      <button className="aet-icon-btn" title="Preview" onClick={() => handlePreview(t)}>
                        <Monitor size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {drawer && (
        <TemplateDrawer
          template={drawer}
          isEdit={!!drawer.id}
          saving={saving}
          onChange={updateField}
          onClose={closeDrawer}
          onSave={submit}
        />
      )}

      {previewHtml !== null && (
        <PreviewModal
          html={previewHtml}
          loading={previewLoading}
          onClose={() => setPreviewHtml(null)}
        />
      )}

      {showNewsletter && (
        <NewsletterModal
          templates={templates.filter((t) => t.isActive)}
          onClose={() => setShowNewsletter(false)}
          onSend={async (slug, subject) => {
            try {
              const res = await adminSendNewsletter(slug, subject);
              const count = res?.count ?? res?.sent ?? 0;
              showNotification(`${count} emails sent.`, 'success');
              setShowNewsletter(false);
            } catch (e) {
              showNotification(e.message || 'Send failed', 'error');
            }
          }}
        />
      )}
    </div>
  );
};

const SmtpNotice = () => (
  <div className="aet-notice">
    <div className="aet-notice-ic"><Info size={18} /></div>
    <div className="aet-notice-body">
      <strong>Email sending requires SMTP configuration.</strong>
      <div>Set these in <code>appsettings.json</code> under the <code>Email</code> section:</div>
      <ul>
        <li><code>Email:Smtp:Username</code> — Gmail address</li>
        <li><code>Email:Smtp:Password</code> — Gmail App Password (not your login password)</li>
        <li><code>Email:FromEmail</code></li>
      </ul>
      <div className="aet-notice-hint">
        Create a Gmail App Password: Google Account → Security → 2-Step Verification → App passwords.
      </div>
    </div>
  </div>
);

const TemplateDrawer = ({ template, isEdit, saving, onChange, onClose, onSave }) => (
  <>
    <div className="aet-backdrop" onClick={onClose} />
    <aside className="aet-drawer">
      <header className="aet-drawer-head">
        <div>
          <div className="aet-drawer-kicker">{isEdit ? 'Edit template' : 'New template'}</div>
          <h2>{template.name || 'Untitled'}</h2>
        </div>
        <button className="aet-icon-btn" onClick={onClose}><X size={18} /></button>
      </header>

      <div className="aet-drawer-body">
        <label className="aet-label">Name
          <input
            className="aet-input"
            value={template.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="e.g. Interview Booked"
          />
        </label>
        <label className="aet-label">Slug {isEdit && <span className="aet-hint">(immutable)</span>}
          <input
            className="aet-input"
            value={template.slug}
            onChange={(e) => onChange('slug', e.target.value)}
            placeholder="interview-booked"
            disabled={isEdit}
          />
        </label>
        <label className="aet-label">Subject
          <input
            className="aet-input"
            value={template.subject}
            onChange={(e) => onChange('subject', e.target.value)}
            placeholder="Use {{userName}}, {{planName}} etc."
          />
          <span className="aet-hint">Use double-brace placeholders like <code>{'{{userName}}'}</code>.</span>
        </label>
        <label className="aet-label">Description (optional)
          <textarea
            className="aet-input aet-textarea"
            rows={2}
            value={template.description || ''}
            onChange={(e) => onChange('description', e.target.value)}
          />
        </label>
        <label className="aet-label">HTML Body
          <textarea
            className="aet-input aet-code"
            rows={16}
            value={template.htmlBody || ''}
            onChange={(e) => onChange('htmlBody', e.target.value)}
            placeholder="<html><body>Hello {{userName}}!</body></html>"
            spellCheck={false}
          />
        </label>
        <label className="aet-toggle-row">
          <span>Active</span>
          <button
            type="button"
            className={`aet-switch ${template.isActive ? 'on' : ''}`}
            onClick={() => onChange('isActive', !template.isActive)}
          >
            <span className="aet-switch-thumb" />
          </button>
        </label>
      </div>

      <footer className="aet-drawer-foot">
        <button className="aet-btn aet-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="aet-btn aet-btn-primary" onClick={onSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving…' : 'Save Template'}
        </button>
      </footer>
    </aside>
  </>
);

const PreviewModal = ({ html, loading, onClose }) => (
  <div className="aet-modal-backdrop" onClick={onClose}>
    <div className="aet-modal aet-modal-lg" onClick={(e) => e.stopPropagation()}>
      <header className="aet-modal-head">
        <h3>Preview</h3>
        <button className="aet-icon-btn" onClick={onClose}><X size={18} /></button>
      </header>
      <div className="aet-iframe-wrap">
        {loading && <div className="aet-loading">Rendering preview…</div>}
        <iframe
          title="Template preview"
          className="aet-iframe"
          srcDoc={html}
          sandbox=""
        />
      </div>
    </div>
  </div>
);

const NewsletterModal = ({ templates, onClose, onSend }) => {
  const [slug, setSlug] = useState(templates[0]?.slug || '');
  const [subject, setSubject] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const disabled = !slug || !confirmed;

  const selectedSubject = useMemo(
    () => templates.find((t) => t.slug === slug)?.subject || '',
    [slug, templates]
  );

  return (
    <div className="aet-modal-backdrop" onClick={onClose}>
      <div className="aet-modal" onClick={(e) => e.stopPropagation()}>
        <header className="aet-modal-head">
          <h3>Send Newsletter</h3>
          <button className="aet-icon-btn" onClick={onClose}><X size={18} /></button>
        </header>
        <div className="aet-modal-body">
          <label className="aet-label">Template
            <select
              className="aet-input"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            >
              {templates.length === 0 && <option value="">No active templates</option>}
              {templates.map((t) => (
                <option key={t.id} value={t.slug}>{t.name} ({t.slug})</option>
              ))}
            </select>
          </label>
          <label className="aet-label">Subject override
            <input
              className="aet-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={selectedSubject || 'Leave empty to use template subject'}
            />
          </label>
          <div className="aet-warn">
            ⚠ This will send email to <strong>ALL registered users</strong>.
          </div>
          <label className="aet-confirm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            I understand and want to send this to all users.
          </label>
        </div>
        <footer className="aet-modal-foot">
          <button className="aet-btn aet-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="aet-btn aet-btn-primary"
            disabled={disabled}
            onClick={() => onSend(slug, subject)}
          >
            <Send size={14} /> Send to All
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AdminEmailTemplatesPage;
